/**
 * A linkage template in the record panel: what starts it, its chain link by
 * link with each gate, how many organizations run it (D37), and the way to
 * its canvas. An empty gate is named first, with the step it stops and what
 * to do (Feature 72 D4). Delete is refused by the agent while an
 * organization enables the template or a run of it is open, which the toast
 * says.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  deleteLinkageTemplate,
  listLinkageTemplates,
} from '@optum/api-client/endpoints/linkage-templates';
import type { LinkageTemplate } from '@optum/api-client/types';

import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordListError,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { relativeTime } from '../../lib/format.js';
import { viewerTimeZone } from '../../lib/cron.js';
import { qk } from '../../lib/query-keys.js';
import { LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import { Callout, IconTile, Stack, Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { fromLinkageTemplate } from '../automation/overlaps.js';
import { linkageCanvasPath } from '../automation/paths.js';
import { OverlapNotice } from '../automation/OverlapNotice.js';
import { usePlatformOverlaps } from '../automation/usePlatformOverlaps.js';
import { linkId } from './draft.js';
import { gateOf, gateText } from './gates.js';
import { startWords } from './start.js';

/** The canvas of a template, under the platform's Automation (Feature 74 D10). */
export function canvasPath(key: string): string {
  return linkageCanvasPath(key);
}

function DeleteLinkage({ template }: { template: LinkageTemplate }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const panel = useRecordPanel();
  const remove = useMutation({
    mutationFn: () => deleteLinkageTemplate(template.key),
    onSuccess: () => {
      toast.success(`Deleted ${template.key}`);
      panel.close();
      void qc.invalidateQueries({ queryKey: qk.linkageTemplates() });
    },
    onError: (e) => {
      const inUse = e instanceof ApiError && e.details?.['reason'] === 'in_use';
      toast.error(
        'Delete refused',
        inUse
          ? 'Switch it off in every organization and let its runs end first.'
          : e instanceof ApiError
            ? e.message
            : String(e),
      );
    },
  });
  return (
    <Button
      variant="danger"
      onClick={() => {
        void confirm({
          title: `Delete linkage ${template.key}?`,
          description:
            'The template goes for good. Tasks it already ran keep their linked results.',
          confirmLabel: 'Delete',
          tone: 'danger',
        }).then((ok) => {
          if (ok) remove.mutate();
        });
      }}
    >
      Delete
    </Button>
  );
}

export function LinkageRecord({ record }: { record: RecordRef }) {
  const templates = useQuery({ queryKey: qk.linkageTemplates(), queryFn: listLinkageTemplates });
  const overlapsOf = usePlatformOverlaps();
  if (templates.isPending) return <LoadingBlock />;
  if (templates.isError) {
    return (
      <RecordListError
        message={templates.error instanceof Error ? templates.error.message : 'Failed to load'}
      />
    );
  }
  const t = templates.data.find((row) => row.key === record.id);
  if (!t) return <RecordMissing record={record} />;
  const organizations = t.enabled_organizations;
  const empty = t.empty_gates ?? [];

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={
          <IconTile icon="account_tree" size={40} tint="var(--blue-tint)" color="var(--blue)" />
        }
        eyebrow={<Typography variant="eyebrow">Linkage template</Typography>}
        name={t.name}
        subtitle={<span className="mono">{t.key}</span>}
      />
      <OverlapNotice self={fromLinkageTemplate(t)} others={overlapsOf(t.key)} templates />
      {empty.length > 0 && (
        <Callout tone="warn" label={`${empty.length} empty gate${empty.length === 1 ? '' : 's'}`}>
          <Stack gap={7}>
            {t.graph.links
              .filter((l) => empty.includes(linkId(l.from, l.to)))
              .map((l) => (
                <span key={linkId(l.from, l.to)}>
                  <span className="mono">{`${l.from} → ${l.to}`}</span> has no condition and does
                  not pass automatically, so <span className="mono">{l.to}</span> never runs. Give
                  it a condition, or turn Pass automatically on.
                </span>
              ))}
          </Stack>
        </Callout>
      )}
      <RecordSection label="Runs">
        <RecordFacts
          items={[
            {
              label: 'Starts',
              value: (() => {
                const start = startWords(t.start, t.first_capability_id, viewerTimeZone());
                return start.cron ? `${start.words} · ${start.cron}` : start.words;
              })(),
            },
            { label: 'Steps', value: <span className="mono">{t.step_count}</span> },
            {
              label: 'Enabled in',
              value:
                organizations === 0 ? (
                  'no organization'
                ) : (
                  <>
                    <span className="mono">{organizations}</span>
                    {` organization${organizations === 1 ? '' : 's'}`}
                  </>
                ),
            },
            {
              label: 'Updated',
              value: <span className="mono">{relativeTime(t.updated_at)}</span>,
            },
          ]}
        />
      </RecordSection>
      <RecordSection label="Chain">
        <Stack gap={5}>
          {t.graph.links.map((l) => {
            const gate = gateOf(l);
            // A link out of a decision is its branch: `tier · hot → suggest` (Feature 79).
            if (gate.kind === 'branch') {
              return (
                <span key={linkId(l.from, l.to)} className="mono">
                  {`${l.from} · ${gate.name} → ${l.to}`}
                </span>
              );
            }
            return (
              <span key={linkId(l.from, l.to)} className="mono">
                {`${l.from} → ${l.to}`}
                <span
                  style={{
                    color: gate.kind === 'empty' ? 'var(--warn)' : 'var(--text-muted)',
                    fontWeight: gate.kind === 'empty' ? 'var(--weight-semibold)' : undefined,
                  }}
                >{` · ${gateText(gate)}`}</span>
              </span>
            );
          })}
        </Stack>
      </RecordSection>
      <RecordActions>
        <RecordOpen record={record} label="Open canvas" />
        <DeleteLinkage template={t} />
      </RecordActions>
    </RecordColumn>
  );
}
